<template>
    <Calendar
        v-if="standaloneCalendar"
        v-model="selectedDate"
        v-bind="forwarded"
        initialFocus
        :placeholder="calendarPlaceholder"
    />

    <DsPopover
        v-else
        v-model:open="openPicker"
    >
        <DsPopoverTrigger>
            <slot name="trigger">
                <DsTextInput
                    v-bind="input"
                    class="min-w-max cursor-pointer"
                    :disabled="disabled"
                    :error="input?.error"
                    :forceFocusOutline="openPicker"
                    inputWrapperClasses="user-select-none pointer-events-none"
                    :modelValue="extractedDate"
                    readonly
                    :tabindex="-1"
                    type="date"
                    @click.stop="textInputClicked"
                    @clickHelperText="emit('clickHelperText')"
                >
                    <template
                        v-if="$slots.label"
                        #label
                    >
                        <slot name="label" />
                    </template>
                    <template
                        v-if="$slots.labelTooltip"
                        #labelTooltip
                    >
                        <slot name="labelTooltip" />
                    </template>
                    <template #trailing>
                        <span class="flex items-center gap-1">
                            <DsIconButton
                                v-if="selectedDate"
                                class="pointer-events-auto"
                                :disabled="props.disabled"
                                icon="fa-solid fa-circle-xmark"
                                size="xs"
                                tooltipText="Clear date"
                                @click.stop="selectedDate = undefined"
                            />

                            <DsIconButton
                                class="pointer-events-auto"
                                :disabled="props.disabled"
                                icon="fa-regular fa-calendar"
                                size="xs"
                                tooltipText="Open calendar"
                                @click.stop="openPicker = true"
                            />
                        </span>
                    </template>
                </DsTextInput>
            </slot>
        </DsPopoverTrigger>
        <DsPopoverContent
            align="start"
            :sideOffset="hasMessage ? -20 : 4"
        >
            <Calendar
                v-model="selectedDate"
                v-bind="forwarded"
                initialFocus
                :placeholder="calendarPlaceholder"
            />
        </DsPopoverContent>
    </DsPopover>
</template>

<script setup lang="ts">
import { notifyError } from '@/bugsnag';
import DsIconButton from '@/components/design-system/button/DsIconButton.vue';
import Calendar from '@/components/design-system/form/date-picker/BaseCalendar.vue';
import DsPopover from '@/components/design-system/popover/DsPopover.vue';
import DsPopoverContent from '@/components/design-system/popover/DsPopoverContent.vue';
import DsPopoverTrigger from '@/components/design-system/popover/DsPopoverTrigger.vue';
import type { HelperMessageEmits } from '@/components/design-system/utils/helper-message.types';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import type { DsTextInputProps } from '@ds/form/input/text/text-input.types';
import { getLocalTimeZone, today } from '@internationalized/date';
import { reactiveOmit } from '@vueuse/core';
import type { CalendarRootEmits, CalendarRootProps, DateValue } from 'reka-ui';
import { useForwardPropsEmits } from 'reka-ui';
import { computed, watch, type HTMLAttributes } from 'vue';

export type DsDatePickerProps = Omit<CalendarRootProps, 'modelValue'> & {
    /** Allows control of the underlying DsTextInput component */
    input?: Omit<DsTextInputProps, 'type' | 'readonly' | 'disabled'>;
    disabled?: boolean;
    standaloneCalendar?: boolean;
    class?: HTMLAttributes['class'];
    /** Controls whether the popover should close once a date is selected */
    closeOnSelect?: boolean;
};
export type DsDatePickerEmits = Omit<CalendarRootEmits, 'update:placeholder'> &
    HelperMessageEmits & {
        click: [];
    };

const props = withDefaults(defineProps<DsDatePickerProps>(), {
    standaloneCalendar: false,
    placeholder: () => today(getLocalTimeZone()),
    closeOnSelect: true,
});

const emit = defineEmits<DsDatePickerEmits>();

const delegatedProps = reactiveOmit(props, 'class', 'placeholder');

const selectedDate = defineModel<DateValue>();
const openPicker = defineModel<boolean>('openPicker', { default: false });

const hasMessage = computed(() => !!props.input?.error || !!props.input?.warning || !!props.input?.helperText);

const calendarPlaceholder = computed<DateValue>(() => {
    if (selectedDate.value) {
        const singleDate = Array.isArray(selectedDate.value) ? selectedDate.value[0] : selectedDate.value;
        if (singleDate) {
            return singleDate;
        }
    }
    return props.placeholder ?? today(getLocalTimeZone());
});

const forwarded = useForwardPropsEmits(delegatedProps, emit);

const extractedDate = computed(() => {
    if (!selectedDate.value) {
        return '';
    }

    try {
        const dateValue = selectedDate.value;
        const singleDate = Array.isArray(dateValue) ? dateValue[0] : dateValue;
        if (!singleDate) {
            return '';
        }
        // serializes the date to a string in the format YYYY-MM-DD
        // and leave final formatting to the browser so locale is respected
        return singleDate.toString();
    } catch (error) {
        notifyError(error, 'DsDatePicker:extractedDate');
        return '';
    }
});

const textInputClicked = () => {
    emit('click');
    openPicker.value = !props.disabled;
};

watch(selectedDate, () => {
    if (props.closeOnSelect) {
        openPicker.value = false;
    }
});
</script>
